import React, { useState, useRef, useEffect } from 'react'
import { shape, string, func, bool, oneOfType, any, array } from 'prop-types'
import { connect } from 'react-redux'
import { connectAutoComplete } from 'react-instantsearch-dom'
import { navigate } from 'gatsby'
import { Icon, Input } from '@livechat/design-system-react-components'
import { useDebounce } from 'use-debounce'
import { Search as SearchIcon, Close } from '@livechat/design-system-icons'

import { useOutsideClick } from '@/hooks'
import { KEY_CODES, MAX_SEARCH_RESULTS_COUNT } from '@/constants'
import { getProtocolVersion } from '@/store/selectors'
import { reorderSearch, filterAppsForLicense } from '@/utils/products'
import { decodeQueryString, canUseWindow } from '@/utils/browser'
import Results from './Results'
import styles from './styles'

export const Search = ({
  hits,
  protocolVersion,
  refine,
  onIsExpanded,
  isInputVisible,
  onKeyPress,
  displayAAVersion,
  className,
  placeholder,
  onSelectItem,
  searchInputRef,
  defaultRefinement,
}) => {
  const wrapperRef = useRef(null)
  const resultsRef = useRef(null)
  let inputRef = useRef(null)

  if (searchInputRef) {
    inputRef = searchInputRef
  }

  const [query, setQuery] = useState('')
  const [debouncedQuery] = useDebounce(query, 300)
  const [activeItemIndex, setActiveItemIndex] = useState(null)

  const shouldAllowSearch = query && query.length > 2

  let apps = shouldAllowSearch ? hits : []
  const reorderedHits = reorderSearch(apps)

  apps = filterAppsForLicense(reorderedHits, [], protocolVersion, null)

  useEffect(
    () => {
      if (debouncedQuery) {
        refine(debouncedQuery)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debouncedQuery]
  )

  const clearSearchInfo = () => {
    setQuery('')
    setActiveItemIndex(null)
  }

  const closeSearch = (e) => {
    clearSearchInfo()

    if (e) {
      e.stopPropagation()
    }

    if (onIsExpanded) {
      onIsExpanded(false)
    }
  }

  useOutsideClick(closeSearch, wrapperRef)

  const handleQueryChange = (e) => {
    const query = String(e.currentTarget.value).trimStart()
    setQuery(query)
  }

  const handleScroll = (childIndex) => {
    if (resultsRef.current) {
      const { height: ulHeight, top: ulTop } =
        resultsRef.current.getBoundingClientRect()

      const activeElement = resultsRef.current.children[childIndex]

      if (activeElement) {
        const { height: itemHeight, top: itemTop } =
          activeElement.getBoundingClientRect()

        const relativeTop = itemTop + itemHeight - ulTop
        const itemOfsetTop = activeElement.offsetTop

        if (relativeTop > ulHeight) {
          resultsRef.current.scrollTop = itemOfsetTop - ulHeight + itemHeight
        } else if (itemTop < ulTop) {
          resultsRef.current.scrollTop =
            itemOfsetTop - (itemOfsetTop % itemHeight)
        }
      }
    }
  }

  const onActiveItemChange = (index) => {
    if (onSelectItem) {
      onSelectItem()
    }

    setActiveItemIndex(index)
  }

  const handleKeyDown = (e) => {
    let nextIndex = activeItemIndex

    switch (e.keyCode) {
      case KEY_CODES.ENTER:
        if (shouldAllowSearch) {
          if (query === defaultRefinement) {
            onKeyPress?.()
            closeSearch()
          } else if (activeItemIndex === MAX_SEARCH_RESULTS_COUNT) {
            navigate(`/search/?query=${query}`)
          } else if (activeItemIndex === null || !apps[activeItemIndex]) {
            navigate('/search/?query=' + e.currentTarget.value)
          } else {
            const pickedItem = apps[activeItemIndex]
            navigate(`/apps/${pickedItem.slug}/`)
          }

          clearSearchInfo()
        }

        break

      case KEY_CODES.ARROW_UP:
        nextIndex =
          activeItemIndex && activeItemIndex > 0 ? activeItemIndex - 1 : 0

        setActiveItemIndex(nextIndex)
        handleScroll(nextIndex)
        break

      case KEY_CODES.ARROW_DOWN:
        if (nextIndex === null || !apps) {
          nextIndex = 0
        } else {
          if (apps.length >= MAX_SEARCH_RESULTS_COUNT) {
            nextIndex =
              activeItemIndex >= MAX_SEARCH_RESULTS_COUNT
                ? MAX_SEARCH_RESULTS_COUNT
                : activeItemIndex + 1
          } else {
            nextIndex =
              activeItemIndex >= apps.length - 1
                ? apps.length - 1
                : activeItemIndex + 1
          }
        }

        setActiveItemIndex(nextIndex)
        handleScroll(nextIndex)
        break

      default:
    }
  }

  return (
    <div
      css={styles.autocomplete(isInputVisible, displayAAVersion)}
      ref={wrapperRef}
      onClick={() => {
        if (onIsExpanded) {
          onIsExpanded(true)
        }
      }}
      className={className}
    >
      <Icon
        source={SearchIcon}
        data-test="search-icon"
        onClick={() => {
          inputRef.current.focus()
        }}
      />

      {displayAAVersion && (
        <Icon
          source={Close}
          data-cy="search-close-icon"
          className="search-close-icon"
          onClick={closeSearch}
          size="large"
        />
      )}

      <Input
        id="search"
        value={query}
        ref={inputRef}
        autoComplete="off"
        placeholder={placeholder || 'Search for apps'}
        onChange={handleQueryChange}
        onKeyDown={handleKeyDown}
        css={styles.input(isInputVisible, displayAAVersion)}
      />

      {query?.length > 2 && (
        <Results
          items={apps}
          query={query}
          resultsRef={resultsRef}
          activeItemIndex={activeItemIndex}
          onActiveItemIndexChange={onActiveItemChange}
        />
      )}
    </div>
  )
}

const mapStateToProps = (state) => {
  const protocolVersion = getProtocolVersion(state)
  const installedApps = state.installedApps.items

  return {
    protocolVersion,
    installedApps,
  }
}

Search.propTypes = {
  hits: array,
  protocolVersion: string,
  installedApps: array,
  refine: func,
  onKeyPress: func,
  onIsExpanded: func,
  isInputVisible: bool,
  className: string,
  onSelectItem: func,
  displayAAVersion: bool,
  placeholder: string,
  defaultRefinement: string,
  searchInputRef: oneOfType([func, shape({ current: any })]),
}

const AutocompleteWithInstantSearch = connect(mapStateToProps)(
  connectAutoComplete(Search)
)

export default (props) => {
  const { query: defaultRefinement } = decodeQueryString(
    canUseWindow ? window.location.search : ''
  )

  return (
    <AutocompleteWithInstantSearch
      defaultRefinement={defaultRefinement}
      {...props}
    />
  )
}
